import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "@/App.css";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { useLenis } from "@/hooks/useLenis";
import NavTabs from "@/components/NavTabs";
import Hero from "@/components/Hero";
import Manifesto from "@/components/Manifesto";
import About from "@/components/About";
import Values from "@/components/Values";
import Policies from "@/components/Policies";
import Risorse from "@/components/Risorse";
import Services from "@/components/Services";
import LavoriCategory from "@/components/LavoriCategory";
import Certifications from "@/components/Certifications";
import Partner from "@/components/Partner";
import Marquee from "@/components/Marquee";
import Contact from "@/components/Contact";
import Privacy from "@/components/Privacy";
import PageHeader from "@/components/PageHeader";
import SiteFooter from "@/components/SiteFooter";

const EASE = [0.16, 1, 0.3, 1];

const setMetaContent = (selector, value) => {
  const element = document.head.querySelector(selector);
  if (element) element.setAttribute("content", value);
};

const Site = () => {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("home");

  const navigate = useCallback((next) => {
    setTab(next);
  }, []);

  useEffect(() => {
    const seoKey = tab.startsWith("lavori:") ? "lavori" : tab;
    const seo = t(`seo.${seoKey}`);

    document.documentElement.lang = lang;
    document.title = seo.title;
    setMetaContent('meta[name="description"]', seo.description);
    setMetaContent('meta[property="og:title"]', seo.title);
    setMetaContent('meta[property="og:description"]', seo.description);
    setMetaContent('meta[name="twitter:title"]', seo.title);
    setMetaContent('meta[name="twitter:description"]', seo.description);
    setMetaContent('meta[property="og:locale"]', lang === "it" ? "it_IT" : "en_GB");
  }, [tab, lang, t]);

  useEffect(() => {
    // reset scroll on tab change
    requestAnimationFrame(() => {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    });
  }, [tab]);

  const renderView = () => {
    if (tab.startsWith("lavori:")) {
      const cat = tab.split(":")[1];
      return <LavoriCategory category={cat} />;
    }
    switch (tab) {
      case "home":
        return (
          <>
            <Hero onNavigate={navigate} />
            <Manifesto />
            <Marquee />
          </>
        );
      case "azienda":
        return (
          <>
            <About />
            <Values />
            <Policies />
          </>
        );
      case "risorse":
        return (
          <>
            <PageHeader label={t("risorse.label")} title={t("risorse.title")} intro={t("risorse.intro")} tone="dark" />
            <Risorse />
          </>
        );
      case "servizi":
        return <Services />;
      case "certificazioni":
        return <Certifications />;
      case "partner":
        return (
          <>
            <PageHeader label={t("partner.label")} title={t("partner.title")} intro={t("partner.intro")} tone="dark" />
            <Marquee bare />
            <Partner />
          </>
        );
      case "contatti":
        return <Contact />;
      case "privacy":
        return <Privacy />;
      default:
        return <Hero />;
    }
  };

  return (
    <div className="App relative bg-cream min-h-screen flex flex-col">
      <div className="grain-overlay" aria-hidden />
      <NavTabs activeTab={tab} onNavigate={navigate} />
      <main className="flex-1 bg-forest-deep">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="min-h-[60vh]"
          >
            {renderView()}
          </motion.div>
        </AnimatePresence>
      </main>
      <SiteFooter onNavigate={navigate} />
    </div>
  );
};

function App() {
  useLenis();
  return (
    <LanguageProvider>
      <Site />
    </LanguageProvider>
  );
}

export default App;
